import {
  StackLayout,
  StackItem,
  ParagraphText,
  UtilityText,
  TextAction,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { TextFormField } from 'components/form-fields/text-form-field/text-form-field.component';
import { useOnboardingError } from 'components/onboarding-account-setup/contexts/onboarding-error.context';
import {
  useOnboardingFormActions,
  useOnboardingFormState,
} from 'components/onboarding-account-setup/contexts/onboarding-form.context';

const renderResendLink = (
  text: React.ReactNode,
  isPending: boolean,
  onResend: () => void,
) => (
  <TextAction
    css={{
      typography: '$bodyTwo',
      fontWeight: '$headingOne',
      opacity: isPending ? 0.5 : 1,
      pointerEvents: isPending ? 'none' : 'auto',
    }}
    aria-disabled={isPending}
    onClick={(e: React.MouseEvent) => {
      e.stopPropagation();
      onResend();
    }}
  >
    {text}
  </TextAction>
);

interface CodeVerificationInputProps {
  serverErrorMessage?: string;
  onCodeChange?: () => void;
}

export const CodeVerificationInput = ({
  serverErrorMessage,
  onCodeChange,
}: CodeVerificationInputProps) => {
  const { formatMessage, messages } = useIntl();
  const { reportError } = useOnboardingError();
  const {
    formState: { verificationCode, verificationCodeError },
    requestOtpStatus,
  } = useOnboardingFormState();
  const { handleVerificationCodeChange, resendOtp } =
    useOnboardingFormActions();

  const localisedError = verificationCodeError
    ? formatMessage({ id: verificationCodeError })
    : null;
  const displayedError = serverErrorMessage || localisedError;

  const handleChange = (value: string) => {
    handleVerificationCodeChange(value);
    onCodeChange?.();
  };

  const handleResend = async () => {
    try {
      await resendOtp();
    } catch (err) {
      reportError(err as Error);
    }
  };

  return (
    <StackLayout spacing="$oneAndHalf">
      <StackItem css={{ gap: '$half' }}>
        <TextFormField
          id="verification-code"
          name="verification-code"
          label={formatMessage({
            id: 'STR_ONBOARDING_ACCOUNT_SETUP_CODE_LABEL',
          })}
          value={verificationCode}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={formatMessage({
            id: 'STR_ONBOARDING_ACCOUNT_SETUP_CODE_PLACEHOLDER',
          })}
          error={displayedError}
          css={{
            maxWidth: '85%',
            '@mobile': { maxWidth: 'unset' },
          }}
          required
        />
        <ParagraphText size="sm">
          {formatMessage(
            { id: 'STR_ONBOARDING_ACCOUNT_SETUP_VERIFY_REQUEST_CODE' },
            {
              a: (text) =>
                renderResendLink(
                  text,
                  requestOtpStatus.isPending,
                  handleResend,
                ),
            },
          )}
        </ParagraphText>
      </StackItem>
      {!!messages.STR_ONBOARDING_ACCOUNT_SETUP_VERIFY_INFO && (
        <ParagraphText
          size="sm"
          css={{ marginBlockEnd: '$none' }}
          htmlStringOptions={{
            strong: {
              component: UtilityText,
              props: {
                size: 'sm',
                css: { fontWeight: '700' },
              },
            },
          }}
        >
          {formatMessage({ id: 'STR_ONBOARDING_ACCOUNT_SETUP_VERIFY_INFO' })}
        </ParagraphText>
      )}
    </StackLayout>
  );
};
